/**
 * Works-lite access-control permissions: the single source of truth (WRK-5473).
 *
 * THE single source of truth for the works-lite action + subject sets. Editing
 * this file re-derives the literal-union types AND the Zod schemas; the engine
 * package (`@skewb/access-control`) holds no values, so it is never rebuilt.
 * Backend reads this via the contracts dist build (normal workflow); works-lite
 * reads it from src via its vite alias. `'all'` (CASL's wildcard) is intentionally
 * NOT a subject — it is an engine concept used only by Admin's blanket grant.
 *
 * This package holds plain TS const-objects + Zod only — it must NOT import
 * `@skewb/access-control` (that would pull `@casl/ability` into the contract
 * layer / FE bundle). The engine and these concrete types meet only in the backend.
 */
import { z } from 'zod';

/**
 * The closed set of actions the access-control matrix supports. Adding an action
 * here extends the whole system (type, guard, matrix, Zod) with no other change.
 *
 * Blanket ("admin") access is NOT a special action. It is modelled by granting
 * every action in this set against CASL's wildcard `all` subject.
 */
export const Action = {
  Create: 'create',
  Read: 'read',
  Update: 'update',
  Delete: 'delete',
} as const;
export type Action = (typeof Action)[keyof typeof Action];

/**
 * The closed subject set the works-lite app authorises against. `'all'` (CASL's
 * wildcard) is intentionally NOT listed — it is an engine concept used only by
 * Admin's blanket grant, not a thing the matrix UI lists.
 */
export const Subject = {
  Job: 'Job',
  Planner: 'Planner',
  WorkOrder: 'WorkOrder',
  Team: 'Team',
  User: 'User',
  Shift: 'Shift',
  Settings: 'Settings',
  AuditTrail: 'AuditTrail',
  AccessControl: 'AccessControl',
  Report: 'Report',
  // WRK-5669: per-entity configuration subjects, split out of the coarse `Settings` umbrella.
  // Backend-enforced by the settings + locations routers.
  JobCategory: 'JobCategory',
  TaskType: 'TaskType',
  Form: 'Form',
  // Export / import of a whole form definition. read = export, create = import.
  FormTransfer: 'FormTransfer',
  Area: 'Area',
  // WRK-5896: backend-enforced on the legacy routes (Discipline/Skill/LeaveType).
  // JobTitle remains frontend-gated only — its legacy API is still ungated.
  JobTitle: 'JobTitle',
  Discipline: 'Discipline',
  Skill: 'Skill',
  LeaveType: 'LeaveType',
} as const;
export type Subject = (typeof Subject)[keyof typeof Subject];

// `string[]` element typing lets `.includes(value)` narrow an arbitrary string
// with no cast (a `readonly ('read' | ...)[]` would reject an unknown string arg).
const ACTION_VALUES: string[] = Object.values(Action);
const SUBJECT_VALUES: string[] = Object.values(Subject);

/** Type guard narrowing an arbitrary string to a known {@link Action}. */
export function isAction(value: string): value is Action {
  return ACTION_VALUES.includes(value);
}

/** Type guard narrowing an arbitrary string to a known {@link Subject}. */
export function isSubject(value: string): value is Subject {
  return SUBJECT_VALUES.includes(value);
}

/** The registered actions as a flat list (declaration order); the closed CRUD set. */
export function listActions(): Action[] {
  return Object.values(Action);
}

/** The registered subjects as a flat list (declaration order); excludes `'all'`. */
export function listSubjects(): Subject[] {
  return Object.values(Subject);
}

/** The closed CRUD action set, derived from {@link Action}. */
export const actionSchema = z.nativeEnum(Action);

/** The closed app subject set, derived from {@link Subject}. Excludes `'all'`. */
export const subjectSchema = z.nativeEnum(Subject);

/**
 * Which `(action, subject)` pairs are actually ENFORCED by a backend route guard.
 *
 * This is the source of truth for the "is this capability real?" question — it mirrors
 * the guard coverage documented in
 * `apps/backend/src/domains/works-lite/access-control/ACCESS_CONTROL_ENFORCEMENT.md`.
 * The role-matrix UI uses it to disable checkboxes that would grant a no-op, so an admin
 * can't hand out a permission that authorises nothing. Keep it in lock-step with the
 * guards; a pair NOT listed here gates no route today.
 *
 * Notable gaps (all deliberate — see the enforcement doc):
 * - `Job` / `WorkOrder` have no `create`: jobs and work orders are created by CORE (an
 *   external service), never by a Works Lite user.
 * - `User` has no `read`: user reads are open to any authenticated user (ungated).
 * - `WorkOrder` is `update`-only: every work-order action mutates an existing order.
 * - `Report` is `read`-only; `AuditTrail` is `read` + `delete`.
 * - `Shift` is full CRUD, backend-enforced on the legacy `/shift-management/*` routes
 *   (WRK-5896 — those routes previously had no ability system and stayed ungated).
 * - `Settings` is now `read`-only (WRK-5669): it gates entry to the settings area (the
 *   `/settings` shell + the legacy-backed sub-pages), NOT any mutation. The CUD it used to
 *   carry moved to the per-entity subjects below.
 * - `JobCategory` / `TaskType` / `Area` are full CRUD, backend-enforced by the settings
 *   (`settings/routes.ts`) and locations (`locations/routes.ts`) routers.
 * - `Form` is full CRUD, backend-enforced on the legacy `/form/*` routes (WRK-5896). (The
 *   task→form assignment routes on `settings/routes.ts` — getTaskForms/setTaskForms — are
 *   separately backend-guarded on Form read+update.)
 * - `FormTransfer` is `create` + `read` only (create = import, read = export), backend-enforced
 *   on `/works-lite/forms/*` and the legacy `/form/export/:id` and `/form/import`.
 * - `Discipline` / `Skill` / `LeaveType` are full CRUD, backend-enforced on the legacy
 *   `/lookup/discipline/*`, `/skill/*`, `/leave-type/*` routes (WRK-5896). The shared
 *   lookup-data reads used outside the maintenance UI (e.g. `/skill/all`, plain
 *   `GET /lookup/discipline`) are deliberately left ungated — they're consumed by every
 *   authenticated user's day-to-day workflows (job-info editing, leave requests, reports),
 *   not just the admin maintenance surface these grants are meant to gate.
 * - `JobTitle` is full CRUD but frontend-gated ONLY, exactly like the others used to be:
 *   its legacy API remains ungated.
 */
const ENFORCED_CAPABILITIES: Readonly<Record<Subject, readonly Action[]>> = {
  [Subject.Job]: [Action.Read, Action.Update, Action.Delete],
  [Subject.WorkOrder]: [Action.Update],
  [Subject.Planner]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.Team]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.User]: [Action.Create, Action.Update, Action.Delete],
  [Subject.Shift]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.Settings]: [Action.Read],
  [Subject.AuditTrail]: [Action.Read, Action.Delete],
  [Subject.AccessControl]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.Report]: [Action.Read],
  [Subject.JobCategory]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.TaskType]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.Form]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.FormTransfer]: [Action.Create, Action.Read],
  [Subject.Area]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.JobTitle]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.Discipline]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.Skill]: [Action.Create, Action.Read, Action.Update, Action.Delete],
  [Subject.LeaveType]: [Action.Create, Action.Read, Action.Update, Action.Delete],
};

/**
 * Whether `(action, subject)` is enforced by a backend route guard — i.e. granting it
 * actually authorises something. Unknown subjects return `false` (fail-safe: an unmapped
 * subject is treated as gating nothing until it's added above).
 */
export function isCapabilityEnforced(action: string, subject: string): boolean {
  const actions = (ENFORCED_CAPABILITIES as Record<string, readonly Action[]>)[subject];
  return actions !== undefined && (actions as readonly string[]).includes(action);
}
